import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { List } from 'immutable';
import { subscribeListener, unsubscribeListener } from 'redux/ably/sagas';
import { MenuSelect, OverlayMenu } from '../chart-components';
import { StudyModal } from '../chart-components/modals';
import { IChartIQData } from '../Quotefeed';
import { IStudyGroup } from 'models/study-group';
import StudyGroupService from 'services/study-group.service';

const CIQ = (window as any).CIQ;
const $$$ = (window as any).$$$;
(window as any)._ = _;

CIQ.Studies.studyLibrary.signalAnalysis = {
  calculateFN: (stx: any, sd: any) => {
    const quotes = sd.chart.scrubbed;
    if (!quotes) { return };
    quotes.forEach((quote: IChartIQData) => {
      if (quote.Signal10_action === 1) {
        quote['Active Buy ' + sd.name] = quote.Low;
      }
      if (quote.Signal90_action === -1) {
        quote['Active Sell ' + sd.name] = quote.High;
      }
      if (quote.SignalStop_action === -2) {
        quote['Early Stop ' + sd.name] = quote.High;
      }
    });
  },
  inputs: {},
  name: '_Signals',
  outputs: {
    'Active Buy': '#00FF00',
    'Active Sell': '#FF0000',
    'Early Stop': '#ffff00',
  },
  overlay: true,
  seriesFN: (stx: any, sd: any, quotes: any) => {
    const panel = stx.panels[sd.panel];
    const yAxis = stx.getYAxisByName(panel, sd.name);
    const sharingChartAxis = panel.name === panel.chart.name && !yAxis;
    stx.startClip(panel.name);
    const ctx = stx.chart.context;
    const squareWave = sd.inputs['Plot Type'] === 'squarewave';
    Object.keys(sd.outputs).forEach(output => {
      let translation = 0
      let translationSign = 1
      switch (output) {
        case 'Active Buy':
          translation = 15
          translationSign = 1
          break;
        case 'Active Sell':
          translation = -15
          translationSign = -1
          break;
        case 'Early Stop':
          translation = -45
          translationSign = -1
          break;
      }
      let color = CIQ.Studies.determineColor(sd.outputs[output]);
      const field = output + ' ' + sd.name;
      let candleWidth = stx.layout.candleWidth;
      const pointWidth = Math.min(Math.max(8, Math.floor(stx.chart.tmpWidth / 2)), 15);
      for (let x = 0; x < quotes.length; x++) {

        let quote = quotes[x];
        if (!quote || (!quote[field] && quote[field] !== 0)) { continue }
        if (quote.candleWidth) { candleWidth = quote.candleWidth }
        if (sharingChartAxis && quote.transform) { quote = quote.transform }
        let x0 = stx.pixelFromBar(x, panel.chart);
        if (squareWave) { x0 -= candleWidth / 2 }
        const y0 = stx.pixelFromTransformedValue(quote[field], panel, yAxis);
        ctx.fillStyle = color;
        ctx.font = "bold 16px Lato, sans-serif";
        ctx.strokeStyle = color;
        ctx.lineWidth = pointWidth * 0.1;
        ctx.stxCircle(x0, y0 + translation, pointWidth, false);
        ctx.stxCircle(x0, y0 + translation, pointWidth * 0.6, true);
        const textWidth = ctx.measureText(quote[field]).width;
        ctx.fillText(quote[field], x0 - (textWidth / 2), y0 + translation + ((pointWidth + 8) * translationSign));
      }
      ctx.lineWidth = 1;
      if (sd.highlight) { ctx.lineWidth = 3 }
      if (color === 'auto') { color = stx.defaultColor } // This is calculated and set by the kernel before draw operation.
    });
    stx.endClip();
  },
};

interface IStudiesIQParams {
  ciq?: any,
  saveLayout: () => void
}

interface IStateProps {
  id: number,
  events: List<any>,
  authToken: string,
  keycloak: any
}

interface IDispatchProps {
  fetchEvents: (token: string) => void,
  appendEvent: (event: any) => void,
}

type Props = IStateProps & IDispatchProps & IStudiesIQParams

export const StudiesIQ: React.FunctionComponent<Props> = ({ id, ciq, saveLayout, events, authToken, keycloak, fetchEvents, appendEvent }) => {
  const [activeStudies, setActiveStudies] = useState<any[]>([]);
  const [studyList, setStudyList] = useState<any[]>([]);
  const [showStudyModal, setShowStudyModal] = useState(false);
  const [studyHelper, setStudyHelper] = useState<any>({});
  const [studyOverlay, setStudyOverlay] = useState({ show: false, top: 0, left: 0 });

  // This variable is for determinate event color from category property, therefore...
  // eslint-disable-next-line
  const categoryColor = [
    { background: '#a50026', color: '#ffffff' },
    { background: '#d73027', color: '#ffffff' },
    { background: '#f46d43', color: '#ffffff' },
    { background: '#fdae61', color: '#2d2d2d' },
    { background: '#fee090', color: '#2d2d2d' },
    { background: '#e0f3f8', color: '#2d2d2d' },
    { background: '#abd9e9', color: '#2d2d2d' },
    { background: '#74add1', color: '#ffffff' },
    { background: '#4575b4', color: '#ffffff' },
    { background: '#313695', color: '#ffffff' },
  ];
  const ciqHasStudies = ciq && ciq.layout.studies;

  // This effect run when the component is rendered and set activeStudies and studyList for MenuSelect component
  useEffect(() => {
    if (ciqHasStudies) {

      const hasAccessToStudy = (studyName: string) => {
          const group: IStudyGroup = StudyGroupService.getStudyGroup(studyName);
          return !group || keycloak.hasRole([group.name]);
        }

      const studyNames = Object.values(ciq.layout.studies).filter((studyDescriptor: any) => {
        const study = CIQ.Studies.studyLibrary[studyDescriptor.study.name] || {};
        return hasAccessToStudy(study);
      }).map((studyDescriptor: any) => studyDescriptor.study.name);

      setActiveStudies(studyNames);

      setStudyList(
        Object.keys(CIQ.Studies.studyLibrary)
          .filter((key: string) => hasAccessToStudy(key))
          .map(key => CIQ.Studies.studyLibrary[key])
          .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
      );
    }
  }, [ciq, ciqHasStudies]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (ciqHasStudies) {
      const studyNames = Object.keys(ciq.layout.studies)

      if (studyNames.includes('\u200cevents\u200c')) {
        if (events && events.size > 0) {
          // Let's remove all markers for re-draw again with the new info
          CIQ.Marker.removeByLabel(ciq, 'events');
          // Each event will draw into event panel like a marker
          events.forEach(event => {
            const text = event.getIn(['message', 'text']);
            const categoryNumber = Number.isSafeInteger(event.get("category")) ? event.get("category") : 0;
            const { background, color } = categoryColor[categoryNumber];
            let markerNode = $$$('#eventMarkerPrototype').cloneNode(true);

            markerNode.style.display = 'block';
            markerNode.style['background-color'] = background;
            markerNode.style['color'] = color;
            $$$(".content", markerNode).innerHTML = 'E';
            $$$('.tooltip', markerNode).title = text;
            CIQ.appendClassName(markerNode, 'events');

            new CIQ.Marker({
              stx: ciq,
              xPositioner: "date",
              yPositioner: "none",
              x: new Date(event.get('start_time')),
              label: "events",
              panelName: "\u200cevents\u200c",
              node: markerNode,
            });

            ciq.draw()
          })
        }
      }
    }
  }, [ciq, events, ciqHasStudies, categoryColor]);

  useEffect(() => {
    if (ciqHasStudies) {
      const studyNames = Object.keys(ciq.layout.studies)

      if (studyNames.includes('\u200cevents\u200c')) {
        fetchEvents(authToken);

        subscribeListener('widgets:events:global', id, ({ data }) => {
          appendEvent(data);
        });
      }
    }

    return () => unsubscribeListener('widgets:events:global', id);
  }, [id, ciq, ciqHasStudies, authToken, appendEvent, fetchEvents]);

	useEffect(() => {
		if (ciq) {
      const toggleStudyOverlay = (params: any) => {
        const newState = !showStudyModal;
        setStudyHelper(newState ? new CIQ.Studies.DialogHelper(params) : null);
        setStudyOverlay({ show: newState, top: ciq.cy, left: ciq.cx });
      };

      ciq.callbacks.studyOverlayEdit = toggleStudyOverlay;
      ciq.callbacks.studyPanelEdit = openStudyModal;

      // When a panel of any study is closed, this callback is fired and the panel instance arrive like a parameter
      CIQ.ChartEngine.prototype.append('panelClose', function () {
        const studyNames = Object.values(ciq.layout.studies).map((studyDescriptor: any) => studyDescriptor.study.name);

        setActiveStudies(studyNames);
      });
      // When a study overlay is closed, this callback is fired and the study name arrive like a parameter
      CIQ.ChartEngine.prototype.append('removeOverlay', function () {
        const studyNames = Object.values(ciq.layout.studies).map((studyDescriptor: any) => studyDescriptor.study.name);

        setActiveStudies(studyNames);
      });
    }
	}, [ciq, showStudyModal])

  const addStudyAndSave = (stxx: any, study: any) => {
    /// Possible improvement is to ensure that the study name exists in the list of studies
    const studyKey = Object.keys(CIQ.Studies.studyLibrary).find(key => CIQ.Studies.studyLibrary[key].name === study.name);

    if (activeStudies.includes(study.name)) {
      const studyDescriptor = Object.values(ciq.layout.studies).find((studyDescriptor: any) => studyDescriptor.study.name === study.name);
      CIQ.Studies.removeStudy(ciq, studyDescriptor);

      const studyNames = Object.values(ciq.layout.studies).map((studyDescriptor: any) => studyDescriptor.study.name);
      setActiveStudies(studyNames);
    } else {
      CIQ.Studies.addStudy(ciq, studyKey);
      setActiveStudies([...activeStudies, study.name]);
    }

    saveLayout();
  };

  const openStudyModal = () => {
    setStudyOverlay({ show: false, top: 0, left: 0 });
    setShowStudyModal(true);
  };

  const removeStudyAndSave = () => {
    // Let's remove the study
    CIQ.Studies.removeStudy(studyHelper.stx, studyHelper.sd);
    setStudyOverlay({ show: false, top: 0, left: 0 });
    // Get the study names and set it
    const studyNames = Object.values(ciq.layout.studies).map((studyDescriptor: any) => studyDescriptor.study.name);
    setActiveStudies(studyNames);

    saveLayout();
  };

  const closeStudyModal = () => {
    setStudyOverlay({ show: false, top: 0, left: 0 });
    // setStudyHelper(null);
    setShowStudyModal(false);
  };

  const updateStudyAndSave = (inputs: any, outputs: any, parameters: any) => {
    if(studyHelper.updateStudy) {
      studyHelper.updateStudy({ inputs, outputs, parameters });
    }
    closeStudyModal();
    saveLayout();
  };

  return (
    <span style={{ width: '150px' }}>
      {/* This styled component is a prototype for event markers */}
      <EventMarker id="eventMarkerPrototype">
        <EventMarkerContent className="content" />
        <EventMarkerTooltip className="tooltip" />
      </EventMarker>
      <OverlayMenu studyOverlay={studyOverlay} openStudyModal={openStudyModal} removeStudyAndSave={removeStudyAndSave} />
      <StudyModal
        showStudyModal={showStudyModal}
        studyHelper={studyHelper}
        updateStudyAndSave={updateStudyAndSave}
        closeStudyModal={closeStudyModal}
      />

      <MenuSelect
        hasCheckboxes={true}
        selected={activeStudies}
        options={studyList}
        keyName='name'
        name='name'
        handleOptionSelect={addStudyAndSave}
        needsCiq={true}
        ciq={ciq}
        menuId='studySelect'
        title='Studies'
      />
    </span>
  );
};

const EventMarker = styled.div`
  border-radius: 50%;
  cursor: pointer;
  display: none;
  height: 25px;
  line-height: 25px;
  margin: 5px 0;
  position: absolute;
  text-align: center;
  width: 25px;

  &:hover .tooltip:after {
    background-color: #fff;
    border-radius: 5px;
    top: 28px;
    color: #333;
    left: -10px;
    padding: 5px 15px;
    position: absolute;
    z-index: 98;
    width: 200px;
    content: attr(title);
  }

  &:hover .tooltip:before {
    border: solid;
    border-color: #fff transparent;
    border-width: 0 6px 11px 6px;
    top: 22px;
    content: "";
    left: -10px;
    position: absolute;
    z-index: 99;
  }
`;
const EventMarkerContent = styled.span``;
const EventMarkerTooltip = styled.span`
  display: inline;
  position: relative;
`;

export default StudiesIQ;
